import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import NotificationsPanel from './NotificationsPanel';
import useTheme from '../../hooks/useTheme';
import useLanguage from '../../hooks/useLanguage';


const Header = ({ className = '' }) => {
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showMobileAccountSelector, setShowMobileAccountSelector] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);
  const [selectedMobileField, setSelectedMobileField] = useState('profit');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [logoUrl, setLogoUrl] = useState(null);
  const [faviconUrl, setFaviconUrl] = useState(null);
  const [createAccountData, setCreateAccountData] = useState({
    password: '',
    accountType: '',
    confirmPassword: ''
  });
  const location = useLocation();
  const { isDarkMode, toggleTheme } = useTheme();
  const { currentLanguage, changeLanguage, t, availableLanguages, isRTL } = useLanguage();

  // Account data
  const [accountData, setAccountData] = useState({
    profit: '0.00',
    assets: '1,000,000.00', 
    credit: '1,000,000.00',
    balance: '0.00',
    freeMargin: '1,000,000.00',
    margin: '0.00',
    marginLevel: '0.00'
  });

  // Load logo and favicon from localStorage
  useEffect(() => {
    const storedLogoUrl = localStorage.getItem('app_logo_url');
    const storedFaviconUrl = localStorage.getItem('app_favicon_url');
    
    if (storedLogoUrl) {
      setLogoUrl(storedLogoUrl);
    }
    
    if (storedFaviconUrl) {
      setFaviconUrl(storedFaviconUrl);
      // Update favicon in document head
      const faviconLink = document.querySelector('link[rel="icon"]') || document.createElement('link');
      faviconLink.type = 'image/x-icon';
      faviconLink.rel = 'icon';
      faviconLink.href = storedFaviconUrl;
      document.head?.appendChild(faviconLink);
    }
  }, []);

  // Load accounts on component mount
  useEffect(() => {
    loadAccounts();
    loadNotifications();
  }, []);

  // Load available accounts
  const loadAccounts = async () => {
    try {
      // In a real implementation, this would fetch from the API
      const mockAccounts = [
        { id: 1, name: 'Demo Account', type: 'demo', balance: 10000, currency: 'USD', leverage: '1:100' },
        { id: 2, name: 'Live Account #1', type: 'live', balance: 5000, currency: 'USD', leverage: '1:50' },
        { id: 3, name: 'Live Account #2', type: 'live', balance: 15000, currency: 'EUR', leverage: '1:30' }
      ];
      
      setAvailableAccounts(mockAccounts);
      
      // Set first account as selected by default
      if (mockAccounts?.length > 0) {
        setSelectedAccount(mockAccounts?.[0]);
      }
    } catch (error) {
      console.error('Failed to load accounts:', error);
      // Set fallback account
      const fallbackAccount = { id: 1, name: 'Demo Account', type: 'demo', balance: 10000, currency: 'USD', leverage: '1:100' };
      setAvailableAccounts([fallbackAccount]);
      setSelectedAccount(fallbackAccount);
    }
  };

  // Load notifications
  const loadNotifications = async () => {
    try {
      // Mock notifications - in real app, fetch from API
      const mockNotifications = [
        {
          id: 1,
          type: 'trade',
          title: 'Position Closed',
          message: 'EURUSD position closed with +$125.50 profit',
          timestamp: new Date(Date.now() - 5 * 60 * 1000),
          read: false,
          icon: 'TrendingUp'
        },
        {
          id: 2,
          type: 'alert',
          title: 'Price Alert',
          message: 'GBPUSD reached target price 1.3500',
          timestamp: new Date(Date.now() - 15 * 60 * 1000),
          read: false,
          icon: 'Bell'
        },
        {
          id: 3,
          type: 'news',
          title: 'Market News',
          message: 'Fed announces interest rate decision',
          timestamp: new Date(Date.now() - 30 * 60 * 1000),
          read: true,
          icon: 'Newspaper'
        }
      ];
      
      setNotifications(mockNotifications);
      setUnreadCount(mockNotifications?.filter(n => !n?.read)?.length);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  };

  const handleAccountSwitch = (account) => {
    setSelectedAccount(account);
    setShowAccountDropdown(false);
    
    // Update account data based on selected account
    // In real implementation, fetch actual account data from API
    console.log('Switching to account:', account?.name);
  };

  const handleCreateAccountSubmit = (e) => {
    e?.preventDefault();
    // Validate password confirmation
    if (createAccountData?.password !== createAccountData?.confirmPassword) {
      alert('Parolalar eşleşmiyor!');
      return;
    }
    console.log('Creating new account:', createAccountData);
    // In a real app, this would make an API call to create the account
    setShowCreateAccountModal(false);
    setCreateAccountData({
      password: '',
      accountType: '',
      confirmPassword: ''
    });
    setShowAccountDropdown(false);
  };

  const markNotificationRead = (notificationId) => {
    setNotifications(prev =>
      prev?.map(notification =>
        notification?.id === notificationId
          ? { ...notification, read: true }
          : notification
      )
    );
    
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllNotificationsRead = () => {
    setNotifications(prev =>
      prev?.map(notification => ({ ...notification, read: true }))
    );
    setUnreadCount(0);
  };

  // Format time ago
  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  // Mobile account fields for selectbox
  const mobileAccountFields = [
    { key: 'profit', label: t('profit'), value: accountData?.profit },
    { key: 'assets', label: t('assets'), value: accountData?.assets?.replace('1,000,000.00', '1M') },
    { key: 'credit', label: t('credit'), value: accountData?.credit?.replace('1,000,000.00', '1M') },
    { key: 'balance', label: t('balance'), value: accountData?.balance },
    { key: 'freeMargin', label: t('freeMargin'), value: accountData?.freeMargin?.replace('1,000,000.00', '1M') }
  ];

  const selectedFieldData = mobileAccountFields?.find(field => field?.key === selectedMobileField);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(currentLanguage === 'tr' ? 'tr-TR' : currentLanguage === 'ar' ? 'ar-SA' : 'en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    })?.format(parseFloat(amount?.replace(/,/g, '')));
  };

  const accountTypes = [
    { value: 'live', label: 'Canlı Hesap' },
    { value: 'demo', label: 'Demo Hesap' },
    { value: 'practice', label: 'Pratik Hesap' },
    { value: 'islamic', label: 'İslami Hesap' }
  ];

  return (
    <>
      <header className={`trading-header h-16 ${className} ${isRTL ? 'rtl' : ''}`}>
        <div className="flex items-center justify-between h-full px-4">
          {/* Left: Logo and Account Selector */}
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-4">
              {/* Logo */}
              <div className="flex items-center space-x-2">
                {logoUrl ? (
                  <img 
                    src={logoUrl} 
                    alt="Logo" 
                    className="w-8 h-8 rounded-lg object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className={`w-8 h-8 bg-primary rounded-lg flex items-center justify-center ${logoUrl ? 'hidden' : ''}`}>
                  <Icon name="TrendingUp" size={20} color="white" />
                </div>
                <span className="text-xl font-semibold text-foreground">Zonex Trader</span>
              </div>
              
              {/* Account Selector */}
              <div className="relative">
                <button
                  onClick={() => setShowAccountDropdown(!showAccountDropdown)}
                  className="flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-md hover:bg-muted transition-colors border border-border min-w-[140px]"
                >
                  <div className="flex flex-col items-start min-w-0 flex-1">
                    <span className="text-xs text-muted-foreground">{t('account')}</span>
                    <div className="flex items-center space-x-1">
                      <div className={`w-2 h-2 rounded-full ${selectedAccount?.type === 'live' ? 'bg-green-500' : 'bg-blue-500'}`}></div>
                      <span className="font-medium text-foreground text-xs truncate">
                        {selectedAccount?.name || 'Demo Account'}
                      </span>
                    </div>
                  </div>
                  <Icon name="ChevronDown" size={14} />
                </button>

                {showAccountDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-64 bg-popover border border-border rounded-md shadow-lg z-dropdown animate-fade-in">
                    <div className="p-1 max-h-64 overflow-y-auto">
                      {availableAccounts?.map((account) => (
                        <button
                          key={account?.id}
                          onClick={() => handleAccountSwitch(account)}
                          className={`w-full text-left px-3 py-3 text-sm rounded-md hover:bg-muted transition-colors ${
                            selectedAccount?.id === account?.id ? 'bg-muted text-foreground' : 'text-muted-foreground'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <div className={`w-2 h-2 rounded-full ${account?.type === 'live' ? 'bg-green-500' : 'bg-blue-500'}`}></div>
                              <div className="flex flex-col">
                                <span className="font-medium">{account?.name}</span>
                                <span className="text-xs text-muted-foreground">
                                  {account?.balance?.toLocaleString()} {account?.currency} • {account?.leverage}
                                </span>
                              </div>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded ${
                              account?.type === 'live' ?'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' :'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                            }`}>
                              {account?.type?.toUpperCase()}
                            </span>
                          </div>
                        </button>
                      ))}
                      
                      {/* Divider */}
                      <div className="my-2">
                        <hr className="border-border" />
                      </div>
                      
                      {/* Create Account Button */}
                      <button
                        onClick={() => {
                          setShowCreateAccountModal(true);
                          setShowAccountDropdown(false);
                        }}
                        className="w-full text-left px-3 py-3 text-sm rounded-md hover:bg-muted transition-colors text-primary hover:text-primary/80"
                      >
                        <div className="flex items-center space-x-2">
                          <Icon name="Plus" size={16} />
                          <span className="font-medium">Hesap Oluştur</span>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Navigation Menu */}
            <nav className="hidden md:flex items-center space-x-2">
              <Link 
                to="/transaction"
                className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  location?.pathname === '/transaction' || location?.pathname === '/' || location?.pathname === '/main-trading-dashboard' ?'bg-primary/10 text-primary border border-primary/20' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Icon name="BarChart3" size={16} />
                <span>İşlem</span>
              </Link>
              <Link
                to="/prop" 
                className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  location?.pathname === '/prop' || location?.pathname === '/market-analysis-hub' ?'bg-primary/10 text-primary border border-primary/20' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Icon name="Target" size={16} />
                <span>Prop</span>
              </Link>
              <Link
                to="/explore"
                className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  location?.pathname === '/explore' || location?.pathname === '/portfolio-analytics' ?'bg-primary/10 text-primary border border-primary/20' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Icon name="Check" size={16} />
                <span>Keşfet</span>
              </Link>
            </nav>
          </div>

          {/* Center: Account Information - Updated with color-coded styling */}
          <div className="hidden xl:flex items-center space-x-6">
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-xs">Kar</span>
                  <span className="font-mono font-medium text-foreground text-sm">0.00 USD</span>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-xs">Varlık</span>
                  <span className="font-mono font-medium text-emerald-500 text-sm">1 000 000.00 USD</span>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-xs">Kredi</span>
                  <span className="font-mono font-medium text-emerald-500 text-sm">1 000 000.00 USD</span>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-xs">Bakiye</span>
                  <span className="font-mono font-medium text-foreground text-sm">0.00 USD</span>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-xs">Serbest Teminat</span>
                  <span className="font-mono font-medium text-emerald-500 text-sm">1 000 000.00 USD</span>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-xs">Teminat</span>
                  <span className="font-mono font-medium text-foreground text-sm">0.00 USD</span>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-xs">Teminat Seviyesi</span>
                  <span className="font-mono font-medium text-foreground text-sm">0.00 %</span>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Account Summary - SelectBox for Mobile and Tablet */}
          <div className="xl:hidden relative flex-shrink-0">
            <button
              onClick={() => setShowMobileAccountSelector(!showMobileAccountSelector)}
              className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-2 text-sm font-medium rounded-md hover:bg-muted transition-colors border border-border min-w-[100px] sm:min-w-[120px]"
            >
              <div className="flex flex-col items-start min-w-0">
                <span className="text-xs text-muted-foreground truncate">{selectedFieldData?.label}</span>
                <span className="font-mono font-medium text-foreground text-xs truncate">{selectedFieldData?.value}</span>
              </div>
              <Icon name="ChevronDown" size={12} className="sm:hidden" />
              <Icon name="ChevronDown" size={14} className="hidden sm:inline" />
            </button>

            {showMobileAccountSelector && (
              <div className="absolute top-full left-0 mt-1 w-40 sm:w-48 bg-popover border border-border rounded-md shadow-lg z-dropdown animate-fade-in">
                <div className="p-1 max-h-64 overflow-y-auto">
                  {mobileAccountFields?.map((field) => (
                    <button
                      key={field?.key}
                      onClick={() => {
                        setSelectedMobileField(field?.key);
                        setShowMobileAccountSelector(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors flex flex-col items-start space-y-1 ${
                        selectedMobileField === field?.key ? 'bg-muted text-foreground' : 'text-muted-foreground'
                      }`}
                    >
                      <span className="text-xs text-muted-foreground">{field?.label}</span>
                      <span className="font-mono font-medium">{field?.value}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: Controls */}
          <div className="hidden md:flex items-center space-x-1 flex-shrink-0">
            {/* Language Selector - Desktop only */}
            <div className="relative">
              <button
                onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                className="flex items-center space-x-1 px-2 py-2 text-sm font-medium rounded-md hover:bg-muted transition-colors"
              >
                <span className="text-base">{availableLanguages?.find(lang => lang?.code === currentLanguage)?.flag}</span>
                <span className="text-sm">{availableLanguages?.find(lang => lang?.code === currentLanguage)?.name}</span>
                <Icon name="ChevronDown" size={14} />
              </button>

              {showLanguageDropdown && (
                <div className="absolute top-full right-0 mt-1 w-40 bg-popover border border-border rounded-md shadow-lg z-dropdown animate-fade-in">
                  <div className="p-1">
                    {availableLanguages?.map((language) => (
                      <button
                        key={language?.code}
                        onClick={() => {
                          changeLanguage(language?.code);
                          setShowLanguageDropdown(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors flex items-center space-x-2 ${
                          currentLanguage === language?.code ? 'bg-muted text-foreground' : 'text-muted-foreground'
                        }`}
                      >
                        <span>{language?.flag}</span>
                        <span>{language?.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Theme Toggle - Desktop only */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleTheme}
              className="w-8 h-8 p-0 flex-shrink-0"
            >
              <Icon name={isDarkMode ? "Sun" : "Moon"} size={16} />
            </Button>

            {/* Notifications - Desktop only */}
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowNotifications(!showNotifications)}
                className="w-8 h-8 p-0 relative flex-shrink-0"
              >
                <Icon name="Bell" size={16} />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center leading-none">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Create Account Modal */}
      {showCreateAccountModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Yeni Hesap Oluştur</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="X"
                  onClick={() => setShowCreateAccountModal(false)}
                />
              </div>
            </div>

            <form onSubmit={handleCreateAccountSubmit} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hesap Türü
                  </label>
                  <select
                    value={createAccountData?.accountType}
                    onChange={(e) => setCreateAccountData({...createAccountData, accountType: e?.target?.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Hesap türünü seçin</option>
                    {accountTypes?.map((type) => (
                      <option key={type?.value} value={type?.value}>
                        {type?.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Parola
                  </label>
                  <input
                    type="password"
                    value={createAccountData?.password}
                    onChange={(e) => setCreateAccountData({...createAccountData, password: e?.target?.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Hesap parolasını girin"
                    required
                    minLength="8"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Parola Onayı
                  </label>
                  <input
                    type="password"
                    value={createAccountData?.confirmPassword}
                    onChange={(e) => setCreateAccountData({...createAccountData, confirmPassword: e?.target?.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Parolayı tekrar girin"
                    required
                    minLength="8"
                  />
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateAccountModal(false)}
                  className="flex-1"
                >
                  İptal
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Hesap Oluştur
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Notifications Panel */}
      <NotificationsPanel
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
        notifications={notifications}
        unreadCount={unreadCount}
        onMarkAllRead={markAllNotificationsRead}
        onMarkRead={markNotificationRead}
      />
      {/* Click outside handlers */}
      {(showAccountDropdown || showLanguageDropdown || showMobileAccountSelector || showUserMenu) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowAccountDropdown(false);
            setShowLanguageDropdown(false);
            setShowMobileAccountSelector(false);
            setShowUserMenu(false);
          }}
        />
      )}
    </>
  );
};

export default Header;